import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { fallbackTheme } from '../theming/fallbackTheme';
import { focusStyles } from '../utils/focusStyles';
import { styled, CSSObject } from '../theming';
import { CardProps, CardColor, PaddingProp } from './types';

enum CardState {
  default = 'default',
  hover = 'hover',
  active = 'active',
}

const isPredefinedColorSet = (color: string) =>
  ['default', 'lightened', 'emphasis', 'inverse'].includes(color);

const colorSets = (theme: BabbelTheme, color: string, state: CardState, disabled: boolean) => {
  const { color: themeColor } = fallbackTheme(theme);

  const disabledColor = themeColor.disabled.default;

  if (disabled) {
    return {
      default: disabledColor,
      hover: disabledColor,
      active: disabledColor,
      disabled: disabledColor,
    }[state];
  }

  if (!isPredefinedColorSet(color)) {
    return {
      default: color,
      hover: color,
      active: color,
    }[state];
  }

  const predefinedColorSets = {
    default: {
      default: themeColor.accentInverse.enabledDarkened,
      hover: themeColor.accentInverse.hover,
      active: themeColor.accentInverse.active,
    },
    lightened: {
      default: themeColor.accentInverse.enabled,
      hover: themeColor.accentInverse.hover,
      active: themeColor.accentInverse.active,
    },
    emphasis: {
      default: themeColor.brand.enabled,
      hover: themeColor.brand.hover,
      active: themeColor.brand.active,
    },
    inverse: {
      default: themeColor.accent.enabled,
      hover: themeColor.accent.hover,
      active: themeColor.accent.active,
    },
  };

  return predefinedColorSets[color as CardColor][state];
};

const getPadding = (padding: PaddingProp) => {
  if (typeof padding === 'number') return `${padding}px`;
  if (padding === 'large') return tokens.space.x6;
  return tokens.space.x4;
};

export const StyledCard = styled.div<CardProps>`
  ${({ theme, color = 'default', disabled = false, padding = 'default', onClick }): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    flexShrink: '0',
    boxSizing: 'border-box',
    overflow: 'hidden',
    background: colorSets(theme, color, CardState.default, disabled),
    borderRadius: tokens.radius.large,
    outline: 'none',
    padding: getPadding(padding),
    ...(onClick && {
      ...focusStyles({ type: 'shadow' }),
      ...(disabled && {
        pointerEvents: 'none',
      }),
      '&:hover': {
        ...(isPredefinedColorSet(color)
          ? { backgroundColor: colorSets(theme, color, CardState.hover, disabled) }
          : { backgroundImage: 'linear-gradient(rgb(0 0 0/10%) 0 0)' }), // workaround to make custom color hover state darker
        cursor: 'pointer',
      },
      '&:active': {
        ...(isPredefinedColorSet(color)
          ? { backgroundColor: colorSets(theme, color, CardState.active, disabled) }
          : { backgroundImage: 'linear-gradient(rgb(0 0 0/20%) 0 0)' }), // workaround to make custom color active state darker
        cursor: 'pointer',
      },
    }),
  })}
`;

/**
 * @deprecated
 * PathCard is deprecated and will be removed in the next major version
 */
export const PathCard = styled(StyledCard)({
  borderRadius: '0 32px 32px 32px ',
});
